/**
 * Order Context Persistence
 * 
 * Maps transient AI parsing metadata (fulfillment_type, missing_info_reasons)
 * into existing DB columns (context_tag, comments) so operators retain
 * visibility after save.
 * 
 * Uses a stable [system-context] block in comments to allow replacement
 * on re-save without duplication.
 */

import type { FulfillmentType, MissingInfoReason } from "@/types/order";

const CONTEXT_BLOCK_START = "[system-context]";
const CONTEXT_BLOCK_END = "[/system-context]";

interface MissingReasons {
  phone?: MissingInfoReason;
  address?: MissingInfoReason;
}

// Labels in English — these are stored in DB as operator-facing notes
const REASON_LABELS: Record<MissingInfoReason, string> = {
  messenger_chat: "Messenger chat",
  will_provide_later: "Will provide later",
  known_customer: "Known customer",
  pickup_order: "Pickup order",
  not_mentioned: "Not provided",
  not_needed: "Not needed",
};

/**
 * Build the [system-context] block string from parsing metadata.
 * Returns null if there's nothing to persist.
 */
function buildContextBlock(
  fulfillmentType?: FulfillmentType,
  missingReasons?: MissingReasons,
  hasPartialPayment?: boolean,
): string | null {
  const lines: string[] = [];

  if (fulfillmentType === "pickup") {
    lines.push("📦 Pickup order");
  }

  if (missingReasons?.phone) {
    lines.push(`📱 Phone: ${REASON_LABELS[missingReasons.phone]}`);
  }

  if (missingReasons?.address && fulfillmentType !== "pickup") {
    lines.push(`📍 Address: ${REASON_LABELS[missingReasons.address]}`);
  }

  if (hasPartialPayment) {
    lines.push("💰 Partial payment detected");
  }

  if (lines.length === 0) return null;

  return `${CONTEXT_BLOCK_START}\n${lines.join("\n")}\n${CONTEXT_BLOCK_END}`;
}

/**
 * Strip any existing [system-context] block from comments.
 */
function stripContextBlock(comments: string): string {
  const startIdx = comments.indexOf(CONTEXT_BLOCK_START);
  if (startIdx === -1) return comments;

  const endIdx = comments.indexOf(CONTEXT_BLOCK_END);
  if (endIdx === -1) return comments;

  const before = comments.slice(0, startIdx).trimEnd();
  const after = comments.slice(endIdx + CONTEXT_BLOCK_END.length).trimStart();

  return [before, after].filter(Boolean).join("\n");
}

/**
 * Merge the system-context block into existing comments.
 * Replaces any existing block to prevent duplication.
 */
export function mergeContextIntoComments(
  existingComments: string | null | undefined,
  fulfillmentType?: FulfillmentType,
  missingReasons?: MissingReasons,
  hasPartialPayment?: boolean,
): string | null {
  const block = buildContextBlock(fulfillmentType, missingReasons, hasPartialPayment);
  const cleanComments = existingComments ? stripContextBlock(existingComments) : "";

  if (!block && !cleanComments) return null;
  if (!block) return cleanComments || null;
  if (!cleanComments) return block;

  return `${cleanComments}\n\n${block}`;
}

/**
 * Determine the context_tag value based on fulfillment type.
 * Only auto-sets for pickup; does not override existing tags.
 */
export function resolveContextTag(
  existingTag: string | null | undefined,
  fulfillmentType?: FulfillmentType,
): string | null {
  if (existingTag) return existingTag; // never override user-set tag
  if (fulfillmentType === "pickup") return "Pickup";
  return null;
}
